import nxModule from 'nxModule';
import {ILocationService} from 'angular';
import {NxRouteService} from 'routes/NxRouteService';
import systemPropertyService from 'system/systemPropertyService';
import {HttpService} from 'shared/utils/httpService';
import {CommandService} from 'shared/utils/command/command.types';
import {Confirmation} from 'shared/common/confirmation.types';
import {CustomerCache} from 'components/service/customer.cache.types';
import {Account, ProductDefinition} from 'components/service/product.types';
import {ProductDefinitionService} from 'components/service/product-definition.service';

import templateUrl from './edit-fee-sharing.template.html';
import './edit-fee-sharing.style.less';

export interface AccountFeeSharingDetails {
  feeDefinitionId: number;
  feeName: string;
  partnerSharePercentage: number;
  bankSharePercentage: number;
  targetAccountId: number | null;
  targetProductNumber: string | null;
}

interface FeeSharingEntry {
  feeDefinitionId: number;
  partnerSharePercentage: number;
  targetAccountId: number | null;
}

interface TargetResult {
  id: number;
  productNumber: string;
  productName: string;
  customerName: string;
}

interface Ownership {
  ownershipType: string;
  effectiveName: string;
}

interface AccountSearchResult {
  id: number;
  productNumber: string;
  definitionId: number;
  ownership?: Ownership[];
}

interface AccountSearchResponse {
  result: AccountSearchResult[];
}

export interface FeeSharingRow {
  feeDefinitionId: number;
  feeName: string;
  current: AccountFeeSharingDetails;
  partnerSharePercentage: number;
  targetAccountId: number | null;
  targetProductNumberInput: string;
  targetResults: TargetResult[];
  targetError: string | null;
  // Id of the row's latest target search; a response whose id is no longer current is discarded.
  targetSearchId: number;
}

export class EditFeeSharingController {
  rows: FeeSharingRow[] = [];
  loaded = false;
  productNumberMask: string | null;
  account!: Account;
  productName = '';

  private readonly customerId: string;
  private readonly accountId: string;
  private productDefinitions: ProductDefinition[] = [];

  constructor(
    $route: NxRouteService,
    private $location: ILocationService,
    private http: HttpService,
    private command: CommandService,
    private confirmation: Confirmation,
    private customerCache: CustomerCache,
    private productDefinitionService: ProductDefinitionService
  ) {
    this.customerId = $route.current.params['customerId'] as string;
    this.accountId = $route.current.params['accountId'] as string;
    this.productNumberMask = systemPropertyService.getProperty('PRODUCT_NUMBER_MASK');
  }

  async $onInit(): Promise<void> {
    const [accounts, feeSharing, productDefinitions] = await Promise.all([
      this.customerCache.depositAccounts(this.customerId).toPromise(),
      this.http.get<AccountFeeSharingDetails[]>(`/products/accounts/${this.accountId}/fee-sharing`).toPromise(),
      this.productDefinitionService.toPromise()
    ]);

    this.productDefinitions = productDefinitions || [];
    this.account = accounts.find((a: Account) => a.id === Number(this.accountId))!;
    this.productName = this.account ? this.productNameOf(this.account.definitionId) : '';
    this.rows = (feeSharing || []).map(details => ({
      feeDefinitionId: details.feeDefinitionId,
      feeName: details.feeName,
      current: details,
      partnerSharePercentage: Number(details.partnerSharePercentage || 0),
      targetAccountId: details.targetAccountId,
      targetProductNumberInput: details.targetProductNumber || '',
      targetResults: [],
      targetError: null,
      targetSearchId: 0
    }));
    this.loaded = true;
  }

  // Accounts carry only their product definition id; the name comes from the cached
  // product definitions, the same lookup the deposit accounts list uses.
  productNameOf(definitionId: number): string {
    const definition = this.productDefinitions.find(d => d.id === definitionId);
    return definition ? definition.productName : '';
  }

  bankShare(row: FeeSharingRow): number | null {
    const partnerShare = Number(row.partnerSharePercentage);
    if (row.partnerSharePercentage === null || row.partnerSharePercentage === undefined || isNaN(partnerShare)) {
      return null;
    }
    return 100 - partnerShare;
  }

  isTargetRequired(row: FeeSharingRow): boolean {
    return Number(row.partnerSharePercentage) > 0;
  }

  isRowValid(row: FeeSharingRow): boolean {
    return !this.isTargetRequired(row) || !!row.targetAccountId;
  }

  isValid(): boolean {
    return this.rows.every(row => this.isRowValid(row));
  }

  async searchTarget(row: FeeSharingRow): Promise<void> {
    const searchId = ++row.targetSearchId;
    row.targetResults = [];
    row.targetError = null;
    row.targetAccountId = null;

    const input = row.targetProductNumberInput;
    if (!input || input.length < 3) return;

    const data = await this.http.post<AccountSearchResponse>('/products/accounts/search', {
      productNumber: [input],
      productStatus: ['ACTIVE', 'PENDING'],
      includeOwnership: true,
      pageSize: 5
    }).toPromise();

    // A newer search or a selection happened meanwhile: this response is stale.
    if (searchId !== row.targetSearchId) return;

    const results = data.result || [];
    row.targetResults = results
      .filter(r => r.id !== Number(this.accountId))
      .map(r => {
        const ownerEntry = (r.ownership || []).find(o => o.ownershipType === 'OWNER');
        return {
          id: r.id,
          productNumber: r.productNumber,
          productName: this.productNameOf(r.definitionId),
          customerName: ownerEntry ? ownerEntry.effectiveName : ''
        };
      });

    if (results.length > 0 && row.targetResults.length === 0) {
      row.targetError = 'Target account cannot be the same as the account the fee is collected from.';
    } else if (results.length === 0) {
      row.targetError = 'No PENDING or ACTIVE deposit account found.';
    }
  }

  selectTarget(row: FeeSharingRow, result: TargetResult): void {
    row.targetSearchId++;   // invalidates any search still in flight for this row
    row.targetAccountId = result.id;
    row.targetProductNumberInput = result.productNumber;
    row.targetResults = [];
    row.targetError = null;
  }

  redirect(): void {
    this.$location.path(`/customer/${this.customerId}/accounts/${this.accountId}`);
  }

  redirectBack(): void {
    this.confirmation('Do you want to cancel? Canceling will discard all changes.', () => this.redirect());
  }

  buildEntries(): FeeSharingEntry[] {
    return this.rows.map(row => ({
      feeDefinitionId: row.feeDefinitionId,
      partnerSharePercentage: Number(row.partnerSharePercentage),
      targetAccountId: row.targetAccountId
    }));
  }

  update(): void {
    this.confirmation('Do you want to update the fee sharing?', async () => {
      await this.command.execute('UpdateAccountFeeSharing', {
        productId: Number(this.accountId),
        entries: this.buildEntries()
      }, {nxLoaderText: 'Processing...'}).toPromise();
      this.redirect();
    });
  }
}

nxModule.component('customerAccountEditFeeSharing', {
  templateUrl,
  controller: EditFeeSharingController
});
